import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GripVertical, X, Plus, RotateCcw } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import type { ColumnDef } from "@/lib/columnRegistry";

/**
 * Modal de sélection des colonnes affichées — générique, partagé entre
 * /biens (per-user) et les pages admin-config (Suivi ELS, Planning Photos,
 * Suivi Publication, Apartments for Rent).
 *
 * UX (demande user 2026-05-07) :
 * - 2 colonnes : "Champs affichés" (gauche, ordonnés) / "Champs à ajouter" (droite)
 * - Click sur une carte → la déplace dans l'autre colonne
 * - Sur la colonne de gauche, drag & drop (HTML5 natif) pour réordonner
 * - "Réinitialiser" rétablit les colonnes par défaut du registry passé en prop
 * - "Appliquer" persiste, "Annuler" ferme sans sauver
 *
 * Le registry est passé en prop pour que la modal soit agnostique à la page
 * source (avant 2026-05-08, hardcodé sur BIENS_COLUMNS).
 */
export function ColumnPickerModal<K extends string>({
  isOpen,
  initialSelection,
  registry,
  defaults,
  onClose,
  onSave,
}: {
  isOpen: boolean;
  initialSelection: K[];
  /** Registry complet de la page source — toutes les colonnes proposables. */
  registry: ColumnDef<K>[];
  /** Sélection à appliquer si l'utilisateur clique "Réinitialiser". */
  defaults: K[];
  onClose: () => void;
  onSave: (selection: K[]) => void;
}) {
  const { t } = useTranslation();
  // Draft = liste ordonnée des colonnes affichées. Les non-affichées se déduisent.
  const [draft, setDraft] = useState<K[]>(initialSelection);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  // Réinitialise le draft quand le modal se ré-ouvre avec une nouvelle sélection.
  useEffect(() => {
    if (isOpen) setDraft(initialSelection);
  }, [isOpen, initialSelection]);

  const draftSet = new Set(draft);
  const labelMap = new Map(registry.map((c) => [c.key, t(c.i18nLabel)]));
  const available = registry.filter((c) => !draftSet.has(c.key));

  const remove = (key: K) => {
    setDraft((d) => d.filter((k) => k !== key));
  };
  const add = (key: K) => {
    setDraft((d) => (d.includes(key) ? d : [...d, key]));
  };
  const reset = () => setDraft([...defaults]);

  // ── Drag & drop reorder (HTML5 native, pas de dépendance) ─────────────────
  const onDragStart = (e: React.DragEvent, idx: number) => {
    setDragIndex(idx);
    // Effet visuel + permet le drop
    e.dataTransfer.effectAllowed = "move";
    // Firefox a besoin d'un dataTransfer.setData pour démarrer le drag
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault(); // autorise le drop
    e.dataTransfer.dropEffect = "move";
    if (dropIndex !== idx) setDropIndex(idx);
  };
  const onDragLeaveContainer = (e: React.DragEvent) => {
    // Reset l'indicateur seulement si on quitte la liste entière (pas un sous-élément)
    if (e.currentTarget === e.target) setDropIndex(null);
  };
  const onDrop = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDropIndex(null);
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      return;
    }
    setDraft((d) => {
      const next = [...d];
      const [moved] = next.splice(dragIndex, 1);
      // Ajuste l'index si on dépose après la position d'origine
      const insertAt = dragIndex < idx ? idx - 1 : idx;
      next.splice(insertAt, 0, moved);
      return next;
    });
    setDragIndex(null);
  };
  const onDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("biens.columnPicker.title")}
      className="max-w-3xl"
    >
      <p className="text-sm text-text-secondary mb-4">
        {t("biens.columnPicker.intro")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {/* Champs affichés — ordonnés, réordonnables par drag & drop */}
        <div>
          <h3 className="text-xs font-semibold tracking-wider uppercase text-text-muted mb-2">
            {t("biens.columnPicker.shown")} ({draft.length})
          </h3>
          <div
            className="border border-border rounded-md bg-surface min-h-[200px] max-h-[400px] overflow-y-auto"
            onDragLeave={onDragLeaveContainer}
          >
            {draft.length === 0 ? (
              <p className="text-xs text-text-muted px-3 py-3">
                {t("biens.columnPicker.emptyShown")}
              </p>
            ) : (
              <ul>
                {draft.map((key, idx) => {
                  const label = labelMap.get(key) ?? key;
                  const isDragging = dragIndex === idx;
                  const isDropTarget = dropIndex === idx && dragIndex !== null && dragIndex !== idx;
                  // Indicateur visuel : barre fine au-dessus si on dépose ici en venant d'en bas,
                  // en-dessous si on vient d'en haut.
                  const dropAbove = isDropTarget && (dragIndex ?? 0) > idx;
                  const dropBelow = isDropTarget && (dragIndex ?? 0) < idx;
                  return (
                    <li
                      key={key}
                      draggable
                      onDragStart={(e) => onDragStart(e, idx)}
                      onDragOver={(e) => onDragOver(e, idx)}
                      onDrop={(e) => onDrop(e, idx)}
                      onDragEnd={onDragEnd}
                      className={cn(
                        "flex items-center gap-1 px-2 py-1.5 border-b border-border last:border-b-0 transition-colors select-none",
                        isDragging ? "opacity-40 bg-surface-2" : "hover:bg-surface-2",
                        dropAbove && "border-t-2 border-t-accent",
                        dropBelow && "border-b-2 border-b-accent",
                      )}
                    >
                      <span
                        className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-primary p-1"
                        title={t("biens.columnPicker.dragHint")}
                      >
                        <GripVertical size={14} />
                      </span>
                      <span className="text-xs text-text-muted w-5 text-right tabular-nums">{idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => remove(key)}
                        className="flex-1 text-left text-sm text-text-primary hover:text-accent transition-colors cursor-pointer truncate"
                        title={t("biens.columnPicker.removeHint")}
                      >
                        {label}
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(key)}
                        className="p-1 rounded text-text-muted hover:text-danger transition-colors cursor-pointer"
                        title={t("biens.columnPicker.remove")}
                      >
                        <X size={13} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Champs à ajouter */}
        <div>
          <h3 className="text-xs font-semibold tracking-wider uppercase text-text-muted mb-2">
            {t("biens.columnPicker.available")} ({available.length})
          </h3>
          <div className="border border-border rounded-md bg-surface min-h-[200px] max-h-[400px] overflow-y-auto">
            {available.length === 0 ? (
              <p className="text-xs text-text-muted px-3 py-3">
                {t("biens.columnPicker.emptyAvailable")}
              </p>
            ) : (
              <ul>
                {available.map((c) => (
                  <li
                    key={c.key}
                    className="border-b border-border last:border-b-0"
                  >
                    <button
                      type="button"
                      onClick={() => add(c.key)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-2 hover:text-accent transition-colors cursor-pointer text-left"
                      title={t("biens.columnPicker.addHint")}
                    >
                      <Plus size={13} className="text-text-muted" />
                      <span className="truncate">{t(c.i18nLabel)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-4 mt-4">
        <button
          type="button"
          onClick={reset}
          className="flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors cursor-pointer"
          title={t("biens.columnPicker.reset")}
        >
          <RotateCcw size={13} />
          <span>{t("biens.columnPicker.reset")}</span>
        </button>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={draft.length === 0}>
            {t("biens.columnPicker.apply")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
